import { db, getUnsyncedItems, markAsSynced, getFilesForSyncItem, deleteFilesForSyncItem } from '../utils/db';
import { useOfflineStore } from '../stores/offlineStore';
import {
  markOfflineDailyChecksSynced,
  markOfflineRegisterRecordsSynced
} from '../utils/assetManagementOffline';
import api from '../utils/api';

const MAX_RETRIES = 3;

/**
 * Generate a unique client-side ID for deduplication.
 * Uses crypto.randomUUID() if available, otherwise falls back to timestamp + random.
 */
function generateClientId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Queue an API request for offline sync.
 * Saves to IndexedDB syncQueue and optionally stores file blobs.
 *
 * @param {string} entityType - e.g. 'closeCall', 'siteInduction', 'inspection'
 * @param {string} operation - 'create' | 'update' | 'delete'
 * @param {Object} data - The form/payload data
 * @param {Object} options - { endpoint, method, files: [{ fieldName, file }], projectId }
 * @returns {number} The local syncQueue id
 */
export async function queueRequest(entityType, operation, data, options = {}) {
  const { endpoint, method = 'POST', files = [], projectId } = options;

  // Generate a client-side unique ID for deduplication
  const clientId = generateClientId();

  // Add to syncQueue
  const syncId = await db.syncQueue.add({
    entity_type: entityType,
    operation,
    data: {
      ...data,
      _endpoint: endpoint,
      _method: method,
      _projectId: projectId,
      _clientId: clientId
    },
    client_id: clientId,
    timestamp: new Date().toISOString(),
    synced: 0,
    status: 'pending',
    retry_count: 0
  });

  // Store file blobs if any
  for (const { fieldName, file, photoNote } of files) {
    const blob = file instanceof Blob ? file : new Blob([file]);
    await db.files.add({
      sync_queue_id: syncId,
      field_name: fieldName,
      file_name: file.name || `${fieldName}.bin`,
      mime_type: file.type || 'application/octet-stream',
      photo_note: photoNote || '',
      blob
    });
  }

  // Update pending count in store
  const count = await db.syncQueue.filter(item => item.synced === 0 || item.synced === false).count();
  useOfflineStore.getState().setPendingSyncCount(count);

  // Register for background sync so items are synced even if user closes the tab
  registerBackgroundSync();

  return { syncId, clientId };
}

/**
 * Register for Background Sync via Service Worker.
 * This ensures queued items are synced even if the user closes the browser tab.
 */
function registerBackgroundSync() {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.ready.then((registration) => {
      if ('sync' in registration) {
        registration.sync.register('galldris-sync').catch((err) => {
          console.warn('[syncService] Background sync registration failed:', err);
        });
      }
    }).catch(() => {});
  }
}

/**
 * Process the entire sync queue. Called when coming back online.
 * Processes items FIFO (oldest first). Retries up to MAX_RETRIES.
 */
export async function processQueue() {
  const store = useOfflineStore.getState();

  if (store.syncInProgress) {
    console.log('[syncService] processQueue: already in progress, skipping');
    return; // Already processing
  }

  console.log('[syncService] processQueue: starting...');
  store.setSyncInProgress(true);

  try {
    const items = await getUnsyncedItems();
    console.log(`[syncService] processQueue: found ${items.length} unsynced items`);

    if (items.length === 0) {
      store.setPendingSyncCount(0);
      return;
    }

    // Sort by timestamp (FIFO)
    items.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    // Dedupe drafts per project/type (keep newest only)
    const draftTypes = new Set(['requisitionDraft', 'returnDraft', 'inspectionDraft']);
    const draftDedupeKey = (it) => {
      if (it.entity_type === 'inspectionDraft') {
        return `${it.entity_type}:${it?.data?._projectId || ''}:${it?.data?.template_id || ''}`;
      }
      return `${it.entity_type}:${it?.data?._projectId || ''}`;
    };
    const newestByKey = new Map(); // key -> { id, ts }
    const dropIds = [];
    for (const it of items) {
      if (!draftTypes.has(it.entity_type)) continue;
      const key = draftDedupeKey(it);
      const ts = new Date(it.timestamp).getTime();
      const prev = newestByKey.get(key);
      if (!prev) {
        newestByKey.set(key, { id: it.id, ts });
        continue;
      }
      if (ts > prev.ts) {
        dropIds.push(prev.id);
        newestByKey.set(key, { id: it.id, ts });
      } else {
        dropIds.push(it.id);
      }
    }
    if (dropIds.length) {
      await db.transaction('rw', db.syncQueue, db.files, async () => {
        for (const id of dropIds) {
          await db.files.where('sync_queue_id').equals(id).delete();
          await db.syncQueue.delete(id);
        }
      });
      // remove from local list
      for (let i = items.length - 1; i >= 0; i -= 1) {
        if (dropIds.includes(items[i].id)) items.splice(i, 1);
      }
    }

    const successIds = [];

    for (const item of items) {
      try {
        console.log(`[syncService] Processing item ${item.id}: ${item.entity_type} ${item.operation}`, item.data?._endpoint);
        await processSingleItem(item);
        console.log(`[syncService] Item ${item.id} synced successfully`);
        successIds.push(item.id);

        // Update pending count live
        store.decrementPending();
      } catch (error) {
        console.error(`[syncService] Sync failed for item ${item.id}:`, error);

        // Increment retry count
        const retryCount = (item.retry_count || 0) + 1;
        await db.syncQueue.update(item.id, {
          retry_count: retryCount,
          status: retryCount >= MAX_RETRIES ? 'failed' : 'pending',
          last_error: error.message
        });

        // If network error, stop processing (we're offline again)
        if (error.isOffline) {
          store.setOffline();
          break;
        }
      }
    }

    // Mark successful items
    if (successIds.length > 0) {
      await markAsSynced(successIds);

      // Clean up associated files
      for (const id of successIds) {
        await deleteFilesForSyncItem(id);
      }

      // Clean up local entity tables linked to successfully synced queue items
      try {
        const syncedItems = await db.syncQueue.bulkGet(successIds);
        const clientIds = syncedItems
          .filter(Boolean)
          .map((item) => item.client_id)
          .filter(Boolean);

        await markOfflineRegisterRecordsSynced(clientIds);
        await markOfflineDailyChecksSynced(clientIds);

        // Legacy tables without client_id linkage — only clear when queue is fully drained
        const remainingAfterBatch = await db.syncQueue
          .filter((item) => item.synced === 0 || item.synced === false)
          .count();
        if (remainingAfterBatch === 0) {
          for (const table of ['goodCalls', 'closeCalls', 'ramsDocuments']) {
            if (db[table]) {
              await db[table]
                .filter((item) => item.synced === 0 || item.synced === false)
                .modify({ synced: 1 });
            }
          }
        }
      } catch (cleanupErr) {
        console.warn('Error cleaning up local entity tables after sync:', cleanupErr);
      }
    }

    // Update final count
    const remaining = await db.syncQueue.filter(item => item.synced === 0 || item.synced === false).count();
    store.setPendingSyncCount(remaining);

    if (remaining === 0) {
      store.setLastSyncedAt(new Date().toISOString());
    }
  } finally {
    store.setSyncInProgress(false);
  }
}

/**
 * Process a single queued item by sending it to the API.
 */
async function processSingleItem(item) {
  const { data, entity_type } = item;
  const { _endpoint, _method, _projectId, _clientId, ...payload } = data;

  // Build idempotency header for deduplication
  const idempotencyHeaders = _clientId ? { 'X-Idempotency-Key': _clientId } : {};

  // If there are files associated, reconstruct as FormData
  const files = await getFilesForSyncItem(item.id);

  // Never send system induction with empty/incomplete payload when there are NO files (would overwrite profile with nulls)
  if (entity_type === 'systemInduction') {
    const hasRequired = payload && (payload.first_name || payload.last_name);
    if (!hasRequired && files.length === 0) {
      throw new Error('System induction payload missing required fields (first_name/last_name). Skipping to avoid overwriting profile.');
    }
  }

  // Special handling for inspections: upload files individually first, then submit JSON
  if (entity_type === 'inspection' && files.length > 0) {
    await processInspectionWithFiles(item, payload, files);
    return;
  }

  // Special handling for requisition/return drafts: upload images first, then submit JSON draft
  if ((entity_type === 'requisitionDraft' || entity_type === 'returnDraft') && files.length > 0) {
    await processRequisitionDraftWithFiles(item, payload, files, idempotencyHeaders);
    return;
  }

  // Special handling for inspection closeout: upload closeout attachments, then PUT
  if (entity_type === 'inspectionCloseout') {
    await processInspectionCloseoutWithFiles(item, payload, files, idempotencyHeaders);
    return;
  }

  // Asset management daily check: upload attachments, then submit JSON inspection
  if (entity_type === 'assetDailyCheck') {
    await processAssetDailyCheckWithFiles(item, payload, files, idempotencyHeaders);
    return;
  }

  // Asset management fault close-out: upload attachments, then PATCH
  if (entity_type === 'assetFaultClose') {
    await processAssetFaultCloseWithFiles(item, payload, files, idempotencyHeaders);
    return;
  }

  if (files.length > 0) {
    const formData = new FormData();

    // Add all data fields to FormData
    for (const [key, value] of Object.entries(payload)) {
      if (value !== null && value !== undefined) {
        formData.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
      }
    }

    // Append file blobs
    for (const fileRecord of files) {
      const blob = new Blob([fileRecord.blob], { type: fileRecord.mime_type });
      const file = new File([blob], fileRecord.file_name, { type: fileRecord.mime_type });
      formData.append(fileRecord.field_name, file);
    }

    await api({
      method: _method || 'POST',
      url: _endpoint,
      data: formData,
      headers: {
        ...idempotencyHeaders,
        'Content-Type': 'multipart/form-data'
      }
    });
  } else {
    // Simple JSON request
    await api({
      method: _method || 'POST',
      url: _endpoint,
      data: payload,
      headers: idempotencyHeaders
    });
  }
}

/**
 * Special sync handler for inspections: upload each file individually
 * to /inspections/upload-attachment, then submit the report JSON with file paths.
 */
async function processInspectionWithFiles(item, payload, files) {
  const { _endpoint, _method, _clientId: _cid, ...reportData } = item.data;

  // Upload signature if present
  const signatureFile = files.find(f => f.field_name === 'signature');
  let signatureUrl = null;
  if (signatureFile) {
    const blob = new Blob([signatureFile.blob], { type: signatureFile.mime_type });
    const file = new File([blob], signatureFile.file_name, { type: signatureFile.mime_type });
    const formData = new FormData();
    formData.append('attachment', file);
    const uploadResult = await api.post('/inspections/upload-attachment', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    const sigData = uploadResult.data.data || uploadResult.data;
    signatureUrl = sigData.file_path;
  }

  // Upload item attachments
  const attachmentFiles = files.filter(f => f.field_name.startsWith('attachment_'));
  const uploadedAttachments = {}; // { itemId: [{ file_path, ... }] }

  for (const fileRecord of attachmentFiles) {
    // field_name format: attachment_{itemId}_{index}
    const parts = fileRecord.field_name.split('_');
    const itemId = parts.slice(1, -1).join('_'); // Handle UUIDs with underscores
    const blob = new Blob([fileRecord.blob], { type: fileRecord.mime_type });
    const file = new File([blob], fileRecord.file_name, { type: fileRecord.mime_type });
    const formData = new FormData();
    formData.append('attachment', file);
    const uploadResult = await api.post('/inspections/upload-attachment', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    if (!uploadedAttachments[itemId]) {
      uploadedAttachments[itemId] = [];
    }
    const uploadData = uploadResult.data.data || uploadResult.data;
    uploadedAttachments[itemId].push({
      name: uploadData.file_name || fileRecord.file_name,
      file_path: uploadData.file_path,
      file_size: uploadData.file_size,
      mime_type: uploadData.mime_type || fileRecord.mime_type,
      photo_note: fileRecord.photo_note || ''
    });
  }

  // Rebuild item_answers with uploaded file paths
  const processedItemAnswers = { ...(payload.item_answers || {}) };
  for (const [itemId, uploadedFiles] of Object.entries(uploadedAttachments)) {
    if (processedItemAnswers[itemId]?.reportDetails) {
      processedItemAnswers[itemId] = {
        ...processedItemAnswers[itemId],
        reportDetails: {
          ...processedItemAnswers[itemId].reportDetails,
          attachments: uploadedFiles
        }
      };
    }
  }

  // Submit the report with file paths
  const finalReportData = {
    ...payload,
    item_answers: processedItemAnswers,
    report_details: processedItemAnswers,
    signature_url: signatureUrl
  };

  // Remove internal fields
  delete finalReportData._endpoint;
  delete finalReportData._method;
  delete finalReportData._projectId;

  await api.post('/inspections', finalReportData);
}

/**
 * Special sync handler for requisition/return drafts:
 * upload each queued image via /requisitions/projects/:projectId/uploads,
 * then submit JSON draft with attachments replaced by uploaded metadata.
 */
async function processRequisitionDraftWithFiles(item, payload, files, idempotencyHeaders = {}) {
  const { _endpoint, _method, _projectId } = item.data || {};
  if (!_endpoint || !_projectId) {
    throw new Error('Missing draft sync metadata (_endpoint/_projectId)');
  }

  const uploadedByField = {};
  for (const fileRecord of files) {
    const blob = new Blob([fileRecord.blob], { type: fileRecord.mime_type });
    const file = new File([blob], fileRecord.file_name, { type: fileRecord.mime_type });
    const formData = new FormData();
    formData.append('file', file);

    // eslint-disable-next-line no-await-in-loop
    const uploadResult = await api.post(`/requisitions/projects/${_projectId}/uploads`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    const up = uploadResult?.data?.data || uploadResult?.data || {};
    uploadedByField[fileRecord.field_name] = {
      file_name: up.file_name || fileRecord.file_name,
      file_path: up.file_path,
      file_type: up.file_type || up.mime_type || fileRecord.mime_type,
      file_size: up.file_size
    };
  }

  const nextPayload = { ...payload };
  if (Array.isArray(nextPayload.items)) {
    nextPayload.items = nextPayload.items.map((it) => {
      const atts = Array.isArray(it.attachments) ? it.attachments : [];
      const nextAtts = [];
      for (const a of atts) {
        const ref = a?._offline_ref;
        if (ref && uploadedByField[ref]) {
          nextAtts.push(uploadedByField[ref]);
          continue;
        }
        if (a?.file_path || a?.filePath) {
          nextAtts.push({
            file_name: a?.file_name || a?.fileName,
            file_path: a?.file_path || a?.filePath,
            file_type: a?.file_type || a?.fileType,
            file_size: a?.file_size || a?.fileSize
          });
        }
      }
      return { ...it, attachments: nextAtts };
    });
  }

  await api({
    method: _method || 'POST',
    url: _endpoint,
    data: nextPayload,
    headers: idempotencyHeaders
  });
}

/**
 * Special sync handler for inspection closeout: upload each closeout attachment
 * individually to /inspections/upload-attachment, rebuild the item_answers
 * with server file paths, then PUT the updated inspection.
 */
async function processInspectionCloseoutWithFiles(item, payload, files, idempotencyHeaders = {}) {
  const { inspectionId, itemId, closedBy, closeoutDetail, closeoutAttachmentMeta, item_answers } = payload;

  // Upload closeout attachment files
  const closeoutFiles = files.filter(f => f.field_name.startsWith('closeout_attachment_'));
  const uploadedAttachments = [];

  for (const fileRecord of closeoutFiles) {
    const blob = new Blob([fileRecord.blob], { type: fileRecord.mime_type });
    const file = new File([blob], fileRecord.file_name, { type: fileRecord.mime_type });
    const formData = new FormData();
    formData.append('attachment', file);

    const uploadResult = await api.post('/inspections/upload-attachment', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    const uploadData = uploadResult.data.data || uploadResult.data;
    uploadedAttachments.push({
      name: uploadData.file_name || fileRecord.file_name,
      file_name: uploadData.file_name || fileRecord.file_name,
      file_path: uploadData.file_path,
      file_size: uploadData.file_size || 0,
      mime_type: uploadData.mime_type || fileRecord.mime_type,
      file_type: uploadData.mime_type || fileRecord.mime_type,
      photo_note: fileRecord.photo_note || ''
    });
  }

  // Merge already-uploaded attachments (ones that had file_path already)
  const existingAttachments = (closeoutAttachmentMeta || []).filter(a => a.file_path && !a._offlineIndex && a._offlineIndex !== 0);

  // Rebuild item_answers with the uploaded file paths
  const updatedItemAnswers = { ...(item_answers || {}) };
  if (updatedItemAnswers[itemId]) {
    updatedItemAnswers[itemId] = {
      ...updatedItemAnswers[itemId],
      status: 'closed',
      closedAt: updatedItemAnswers[itemId].closedAt || new Date().toISOString(),
      closedBy: closedBy,
      closeoutDetail: closeoutDetail,
      closeoutAttachments: [...existingAttachments, ...uploadedAttachments]
    };
  }

  // PUT to update the inspection
  await api.put(`/inspections/${inspectionId}`, {
    item_answers: updatedItemAnswers
  }, {
    headers: idempotencyHeaders
  });
}

function isBenignAssetSyncConflict(error) {
  if (error?.response?.status !== 409) return false;
  const message = String(error?.response?.data?.message || '').toLowerCase();
  return (
    message.includes('already been submitted') ||
    message.includes('already checked') ||
    message.includes('already closed') ||
    message.includes('already submitted')
  );
}

/**
 * Upload a file for asset management scan/fault endpoints.
 */
async function uploadAssetManagementAttachment(uploadUrl, fileRecord, clientId) {
  const blob = new Blob([fileRecord.blob], { type: fileRecord.mime_type });
  const file = new File([blob], fileRecord.file_name, { type: fileRecord.mime_type });
  const formData = new FormData();
  formData.append('attachment', file);
  const headers = { 'Content-Type': 'multipart/form-data' };
  if (clientId && fileRecord.field_name) {
    headers['X-Idempotency-Key'] = `${clientId}:${fileRecord.field_name}`;
  }
  const uploadResult = await api.post(uploadUrl, formData, { headers });
  const data = uploadResult.data?.data || uploadResult.data || {};
  return {
    file_name: data.file_name || fileRecord.file_name,
    file_path: data.file_path,
    file_type: data.mime_type || data.file_type || fileRecord.mime_type,
    file_size: data.file_size,
    photo_note: fileRecord.photo_note || ''
  };
}

function resolveAssetAttachmentList(attachments, uploadedByField) {
  return (attachments || [])
    .map((att) => {
      if (att?._offline_ref && uploadedByField[att._offline_ref]) {
        return uploadedByField[att._offline_ref];
      }
      if (att?.file_path) return att;
      return null;
    })
    .filter(Boolean);
}

/**
 * Sync handler for asset daily checks: upload queued files, then POST inspection JSON.
 */
async function processAssetDailyCheckWithFiles(item, payload, files, idempotencyHeaders = {}) {
  const { variant, recordId, responses, signatureData, faults, attachments } = payload;
  if (!variant || !recordId) {
    throw new Error('Asset daily check sync missing variant or recordId');
  }

  const uploadUrl = `/asset-management/scan/${variant}/${recordId}/upload-attachment`;
  const clientId = item.data?._clientId;
  const uploadedByField = {};
  for (const fileRecord of files) {
  // eslint-disable-next-line no-await-in-loop
    uploadedByField[fileRecord.field_name] = await uploadAssetManagementAttachment(
      uploadUrl,
      fileRecord,
      clientId
    );
  }

  const resolvedFaults = (faults || []).map((fault) => ({
    ...fault,
    attachments: resolveAssetAttachmentList(fault.attachments, uploadedByField)
  }));
  const resolvedAttachments = resolveAssetAttachmentList(attachments, uploadedByField);

  try {
    await api.post(
      `/asset-management/scan/${variant}/${recordId}`,
      {
        responses,
        signatureData,
        faults: resolvedFaults,
        attachments: resolvedAttachments
      },
      { headers: idempotencyHeaders }
    );
  } catch (error) {
    if (!isBenignAssetSyncConflict(error)) throw error;
    console.log('[syncService] Asset daily check already on server (409), treating as synced');
  }
}

/**
 * Sync handler for asset fault close-out: upload attachments, then PATCH close endpoint.
 */
async function processAssetFaultCloseWithFiles(item, payload, files, idempotencyHeaders = {}) {
  const { variant, projectId, recordId, faultId, closureNotes, attachments } = payload;
  const pid = projectId || item.data?._projectId;
  if (!variant || !pid || !recordId || !faultId) {
    throw new Error('Asset fault close sync missing required metadata');
  }

  const uploadUrl = `/asset-management/projects/${pid}/${variant}/${recordId}/faults/upload-attachment`;
  const clientId = item.data?._clientId;
  const uploadedByField = {};
  for (const fileRecord of files) {
  // eslint-disable-next-line no-await-in-loop
    uploadedByField[fileRecord.field_name] = await uploadAssetManagementAttachment(
      uploadUrl,
      fileRecord,
      clientId
    );
  }

  const resolvedAttachments = resolveAssetAttachmentList(attachments, uploadedByField);

  try {
    await api.patch(
      `/asset-management/projects/${pid}/${variant}/${recordId}/faults/${faultId}/close`,
      { closureNotes, attachments: resolvedAttachments },
      { headers: idempotencyHeaders }
    );
  } catch (error) {
    if (!isBenignAssetSyncConflict(error)) throw error;
    console.log('[syncService] Asset fault already closed on server (409), treating as synced');
  }
}

/**
 * Also upload items to the backend's sync queue endpoint for server-side processing.
 * This is an alternative sync path using the /api/sync/upload endpoint.
 */
export async function uploadToServerSyncQueue(items) {
  const payload = items.map((item) => ({
    entity_type: item.entity_type,
    entity_id: item.data?.id || null,
    operation: item.operation,
    data: item.data,
    client_timestamp: item.timestamp
  }));

  const response = await api.post('/sync/upload', { data: payload });
  return response.data;
}

/**
 * Get the current count of pending sync items.
 */
export async function refreshPendingCount() {
  const count = await db.syncQueue.filter(item => item.synced === 0 || item.synced === false).count();
  useOfflineStore.getState().setPendingSyncCount(count);
  return count;
}

/**
 * Get the latest pending health/medical assessment payload for a project (from offline queue).
 * Used by Site Induction review to show answers when prefetch cache is empty but user submitted offline.
 * @param {string} projectId
 * @returns {Promise<{ healthAnswers: object, medicalAnswers: object } | null>}
 */
export async function getPendingHealthMedicalAssessment(projectId) {
  if (!projectId) return null;
  try {
    const items = await db.syncQueue
      .where('entity_type')
      .equals('healthMedicalAssessment')
      .filter(item => (item.synced === 0 || item.synced === false) && (item.data?.projectId === projectId || item.data?._projectId === projectId))
      .toArray();
    if (!items.length) return null;
    items.sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0));
    const latest = items[items.length - 1];
    const d = latest?.data || {};
    const healthAnswers = d.healthAnswers || d.health_answers;
    const medicalAnswers = d.medicalAnswers || d.medical_answers;
    if (!healthAnswers && !medicalAnswers) return null;
    return { healthAnswers: healthAnswers || {}, medicalAnswers: medicalAnswers || {} };
  } catch (err) {
    console.warn('[syncService] getPendingHealthMedicalAssessment failed:', err);
    return null;
  }
}

/**
 * Returns true if there is at least one unsynced system induction submission in the queue.
 * Used to allow users to proceed to project induction when they completed system induction offline.
 */
export async function getHasPendingSystemInduction() {
  try {
    const count = await db.syncQueue
      .filter(item => (item.synced === 0 || item.synced === false) && item.entity_type === 'systemInduction')
      .count();
    return count > 0;
  } catch (err) {
    console.warn('[syncService] getHasPendingSystemInduction failed:', err);
    return false;
  }
}

/**
 * Get pending sync count per project ID (for "Waiting to sync" in project rows).
 * @returns {Promise<Record<string, number>>} Map of projectId -> pending count
 */
export async function getPendingSyncCountByProject() {
  try {
    const items = await db.syncQueue
      .filter(item => item.synced === 0 || item.synced === false)
      .toArray();
    const byProject = {};
    for (const item of items) {
      const projectId = item.data?.projectId ?? item.data?._projectId;
      if (projectId) {
        byProject[projectId] = (byProject[projectId] || 0) + 1;
      }
    }
    return byProject;
  } catch (err) {
    console.warn('[syncService] getPendingSyncCountByProject failed:', err);
    return {};
  }
}
